import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { RefreshCw, ChevronDown } from 'lucide-react'
import { isDevMode, getDevAccounts, getCachedDevSession, cacheDevSession } from '../utils/devAccounts'
import { isTokenExpired } from '../utils/jwtDecoder'

const DevAccountSwitcher = () => {
  const { user, login, logout, restoreDevSession } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [switching, setSwitching] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (!isDevMode()) return null

  const accounts = getDevAccounts()
  if (accounts.length === 0) return null

  const handleSwitch = async (account) => {
    setSwitching(true)
    setIsOpen(false)
    logout()

    // Try cached session first
    const cached = getCachedDevSession(account.email)
    if (cached?.token && !isTokenExpired(cached.token)) {
      const restored = restoreDevSession(cached)
      if (restored) {
        navigate('/')
        setSwitching(false)
        return
      }
    }

    // Fall back to full login
    const result = await login({ email: account.email, password: account.password })
    if (result.success) {
      cacheDevSession(account.email, result.data)
      navigate('/')
    }
    setSwitching(false)
  }

  const currentEmail = user?.email?.toLowerCase()

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={switching}
        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors text-amber-700"
      >
        <RefreshCw size={14} className={switching ? 'animate-spin' : ''} />
        <span className="text-xs font-semibold">Dev</span>
        <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
          <div className="px-3 py-2 border-b border-slate-100">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Switch Account</span>
          </div>
          {accounts.map((account) => {
            const isCurrent = currentEmail === account.email.toLowerCase()
            return (
              <button
                key={account.id}
                onClick={() => !isCurrent && handleSwitch(account)}
                disabled={switching}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  isCurrent
                    ? 'bg-amber-50 cursor-default'
                    : 'hover:bg-slate-50 cursor-pointer'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-slate-800">{account.label}</div>
                    <div className="text-xs text-slate-500">{account.email}</div>
                  </div>
                  {isCurrent && (
                    <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                      Current
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default DevAccountSwitcher
